import { fork } from '@cortex/utils/fork';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { EmptyState } from '../../components/common/EmptyState';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { DocsetKgTab } from '../../components/document-sets/DocsetKgTab';
import { Dropzone, type IDroppedFile } from '../../components/document-sets/Dropzone';
import { FileTree, type IFileNode } from '../../components/document-sets/FileTree';
import {
  useCommitDraft,
  useCreateDraft,
  useDeleteDocumentSet,
  useDocumentSet,
  useDocumentSetVersions,
  useLatestVersion,
  useRemoveDraftFile,
  useUploadDraftFile,
} from '../../hooks/useDocumentSets';
import { routes } from '../../lib/constants';
import { formatDateTime, formatRelative } from '../../lib/format';
import {
  fileUrl,
  latestFileUrl,
  type IDocumentSetVersion,
  type IManifestEntry,
} from '../../lib/metaphysics';

import '../../components/document-sets/document-sets.css';

export const Route = createFileRoute('/document-sets/$setId')({ component: DocumentSetDetailPage });

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

/** Per-path overlay state used to drive `IFileNode.state` in the draft tree. */
type StagedState =
  | { kind: 'uploading'; progress: number; file: File }
  | { kind: 'staged'; sizeBytes: number; contentType: string | null }
  | { kind: 'failed'; error: string };

type ITab = 'files' | 'kg';

function DocumentSetDetailPage() {
  const { setId } = Route.useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const set = useDocumentSet(setId);
  const versions = useDocumentSetVersions(setId);
  const latest = useLatestVersion(setId);

  const draft = useMemo<IDocumentSetVersion | null>(() => {
    if (!versions.data) return null;
    return versions.data.find((v) => v.status === 'DRAFT') ?? null;
  }, [versions.data]);

  const createDraft = useCreateDraft(setId);
  const commitDraft = useCommitDraft(setId);
  const removeFile = useRemoveDraftFile(setId, draft?.id);
  const uploadOne = useUploadDraftFile(setId, draft?.id);
  const removeSet = useDeleteDocumentSet();

  // Per-path overlays applied to the baseline-from-latest manifest. Lost on
  // page refresh — see comment further down. Keyed by virtual path.
  const [staged, setStaged] = useState<Map<string, StagedState>>(new Map());
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [busyDeletes, setBusyDeletes] = useState<Set<string>>(new Set());

  const [openCommit, setOpenCommit] = useState(false);
  const [commitName, setCommitName] = useState('');
  const [commitDescription, setCommitDescription] = useState('');

  const [tab, setTab] = useState<ITab>('files');

  function clearStaging() {
    setStaged(new Map());
    setRemoved(new Set());
  }

  // ── Draft creation ────────────────────────────────────────────────────
  async function startDraft(forkFromLatest: boolean) {
    try {
      await createDraft.mutateAsync({
        forkFromVersionId: forkFromLatest ? latest.data?.version.id : undefined,
      });
      clearStaging();
    } catch (err) {
      console.error('document-set/createDraft', err);
    }
  }

  // ── File ops ──────────────────────────────────────────────────────────
  async function handleDropped(items: IDroppedFile[]) {
    if (!draft) return;
    // Upload each file in parallel with a soft concurrency cap. 5 is a
    // pragmatic middle-ground — high enough that a folder of small docs
    // finishes quickly, low enough that one bad file doesn't pin the
    // browser's connection pool.
    const concurrency = 5;
    let cursor = 0;
    async function next() {
      for (;;) {
        const i = cursor;
        cursor += 1;
        const item = items[i];
        if (!item) return;
        await uploadItem(item);
      }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, next));
  }

  async function uploadItem({ path, file }: IDroppedFile) {
    setStaged((prev) => {
      const next = new Map(prev);
      next.set(path, { kind: 'uploading', progress: 0, file });
      return next;
    });
    setRemoved((prev) => {
      if (!prev.has(path)) return prev;
      const next = new Set(prev);
      next.delete(path);
      return next;
    });
    try {
      const entry = await uploadOne(path, file, ({ loaded, total }) => {
        setStaged((prev) => {
          const cur = prev.get(path);
          if (!cur || cur.kind !== 'uploading') return prev;
          const next = new Map(prev);
          next.set(path, { ...cur, progress: total ? loaded / total : 0 });
          return next;
        });
      });
      setStaged((prev) => {
        const next = new Map(prev);
        next.set(path, {
          kind: 'staged',
          sizeBytes: entry.sizeBytes,
          contentType: entry.contentType,
        });
        return next;
      });
    } catch (err) {
      console.error('document-set/upload', path, err);
      setStaged((prev) => {
        const next = new Map(prev);
        next.set(path, { kind: 'failed', error: (err as Error).message });
        return next;
      });
    }
  }

  async function handleDelete(path: string) {
    if (!draft) return;
    // Locally-staged file: cancel the upload optimistic record only.
    const cur = staged.get(path);
    if (cur && cur.kind !== 'staged') {
      setStaged((prev) => {
        const next = new Map(prev);
        next.delete(path);
        return next;
      });
      return;
    }
    // Otherwise fire DELETE against the API.
    setBusyDeletes((prev) => new Set(prev).add(path));
    try {
      await removeFile.mutateAsync(path);
      setStaged((prev) => {
        if (!prev.has(path)) return prev;
        const next = new Map(prev);
        next.delete(path);
        return next;
      });
      // Was previously committed in baseline → mark removed so it disappears
      // from the tree until the user discards or re-uploads.
      setRemoved((prev) => new Set(prev).add(path));
    } catch (err) {
      console.error('document-set/removeFile', path, err);
    } finally {
      setBusyDeletes((prev) => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });
    }
  }

  // ── Commit ────────────────────────────────────────────────────────────
  async function submitCommit() {
    if (!draft || !commitName.trim()) return;
    try {
      await commitDraft.mutateAsync({
        draftId: draft.id,
        name: commitName.trim(),
        description: commitDescription.trim() || undefined,
      });
      setOpenCommit(false);
      setCommitName('');
      setCommitDescription('');
      clearStaging();
    } catch (err) {
      console.error('document-set/commit', err);
    }
  }

  // ── Loading / error guards ────────────────────────────────────────────
  if (set.isLoading) return <Spinner />;
  if (set.error)
    return (
      <div className="alert alert--danger">Failed to load: {(set.error as Error).message}</div>
    );
  if (!set.data) return <EmptyState title="Set not found" />;

  const baselineManifest = latest.data?.manifest ?? [];
  const baselineFiles: IFileNode[] = baselineManifest.map((m) => ({
    path: m.path,
    sizeBytes: m.sizeBytes,
    contentType: m.contentType,
    state: 'committed',
  }));

  // Compose the draft tree: baseline minus removed plus staged.
  const draftFiles: IFileNode[] = (() => {
    const byPath = new Map<string, IFileNode>();
    for (const m of baselineManifest) {
      if (removed.has(m.path)) continue;
      byPath.set(m.path, {
        path: m.path,
        sizeBytes: m.sizeBytes,
        contentType: m.contentType,
        state: 'committed',
      });
    }
    for (const [path, s] of staged) {
      if (s.kind === 'staged') {
        byPath.set(path, {
          path,
          sizeBytes: s.sizeBytes,
          contentType: s.contentType,
          state: 'staged',
        });
      } else if (s.kind === 'uploading') {
        byPath.set(path, {
          path,
          sizeBytes: s.file.size,
          contentType: s.file.type || null,
          state: 'uploading',
          progress: s.progress,
        });
      } else {
        byPath.set(path, {
          path,
          sizeBytes: 0,
          contentType: null,
          state: 'failed',
          detail: s.error,
        });
      }
    }
    return Array.from(byPath.values()).sort((a, b) => a.path.localeCompare(b.path));
  })();

  const stagedCount = Array.from(staged.values()).filter((s) => s.kind === 'staged').length;
  const uploadingCount = Array.from(staged.values()).filter((s) => s.kind === 'uploading').length;
  const failedCount = Array.from(staged.values()).filter((s) => s.kind === 'failed').length;
  const dirty = stagedCount + removed.size > 0;

  function openLatestFile(path: string) {
    if (!latest.data) return;
    window.open(latestFileUrl(setId, path), '_blank', 'noopener,noreferrer');
  }
  function openVersionFile(versionName: string, path: string) {
    window.open(fileUrl(setId, versionName, path), '_blank', 'noopener,noreferrer');
  }

  return (
    <>
      <PageHeader
        title={set.data.name}
        actions={
          <>
            <BackButton to={routes.DOCUMENT_SETS} label="Document sets" />
            <button
              type="button"
              className="btn btn--destructive btn--sm"
              onClick={async () => {
                if (!confirm(`Delete document set "${set.data?.name}"? This cannot be undone.`))
                  return;
                try {
                  await removeSet.mutateAsync(setId);
                  await nav({ to: routes.DOCUMENT_SETS });
                } catch (err) {
                  console.error(err);
                }
              }}
              disabled={removeSet.isPending}
            >
              Delete
            </button>
          </>
        }
      />

      <nav className="tabs" style={{ marginBottom: 'var(--sp-3)' }}>
        {(['files', 'kg'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`tab${tab === t ? ' is-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'files' ? 'Files' : 'Knowledge Graph'}
          </button>
        ))}
      </nav>

      {tab === 'kg' ? <DocsetKgTab setId={setId} /> : null}

      {tab === 'files' ? (
        <div className="docset-layout">
          {/* ── Latest committed (read-only) ───────────────────────────────────── */}
          <section className="docset-panel" aria-label="Latest committed version">
            <header className="docset-panel__header">
              <div className="docset-panel__title">
                Latest committed
                {latest.data ? (
                  <span className="badge badge--neutral docset-version-badge">
                    {latest.data.version.name}
                  </span>
                ) : (
                  <span className="badge badge--neutral docset-version-badge">none</span>
                )}
              </div>
              {latest.data ? (
                <span
                  className="t-xs t-muted"
                  title={formatDateTime(latest.data.version.committedAt)}
                >
                  {formatRelative(latest.data.version.committedAt)}
                </span>
              ) : null}
            </header>
            <div className="docset-panel__body">
              {latest.isLoading ? (
                <Spinner />
              ) : (
                <FileTree
                  files={baselineFiles}
                  onOpenFile={latest.data ? openLatestFile : undefined}
                  emptyState="No committed files yet."
                />
              )}
              {versions.data && versions.data.filter((v) => v.status === 'COMMITTED').length > 1 ? (
                <details>
                  <summary className="t-xs t-muted" style={{ cursor: 'pointer' }}>
                    All committed versions
                  </summary>
                  <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none' }}>
                    {versions.data
                      .filter((v) => v.status === 'COMMITTED')
                      .map((v) => (
                        <li
                          key={v.id}
                          style={{
                            display: 'flex',
                            gap: 'var(--sp-2)',
                            padding: '4px 0',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 12,
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => v.name && openVersionFile(v.name, '')}
                            title={v.description ?? undefined}
                          >
                            {v.name}
                          </button>
                          <span className="t-muted">{formatRelative(v.committedAt)}</span>
                        </li>
                      ))}
                  </ul>
                </details>
              ) : null}
            </div>
          </section>

          {/* ── Working draft (editable) ───────────────────────────────────────── */}
          <section className="docset-panel" aria-label="Working draft">
            <header className="docset-panel__header">
              <div className="docset-panel__title">
                Working draft
                {draft ? (
                  <span className="badge badge--info docset-version-badge">draft</span>
                ) : (
                  <span className="badge badge--neutral docset-version-badge">none</span>
                )}
              </div>
              {draft ? (
                <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={clearStaging}
                    disabled={!dirty || commitDraft.isPending}
                    title="Forget pending uploads/removes (server state is unchanged)"
                  >
                    Reset view
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => setOpenCommit(true)}
                    disabled={uploadingCount > 0 || commitDraft.isPending}
                  >
                    {uploadingCount > 0 ? `Uploading ${uploadingCount}…` : 'Commit…'}
                  </button>
                </div>
              ) : null}
            </header>
            <div className="docset-panel__body">
              {!draft ? (
                <div style={stack3}>
                  <p className="t-secondary" style={{ margin: 0 }}>
                    Create a draft to add or remove files. A draft is a private workspace that
                    becomes a new committed version when you save it.
                  </p>
                  <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => {
                        fork(startDraft(true));
                      }}
                      disabled={createDraft.isPending || !latest.data}
                      title={
                        !latest.data
                          ? 'No committed version to fork from'
                          : 'Fork from latest committed'
                      }
                    >
                      {createDraft.isPending ? 'Creating…' : 'Fork from latest'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={() => {
                        fork(startDraft(false));
                      }}
                      disabled={createDraft.isPending}
                    >
                      Empty draft
                    </button>
                  </div>
                  {createDraft.error ? (
                    <div className="alert alert--danger">
                      Failed to create draft: {(createDraft.error as Error).message}
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  <Dropzone
                    onFiles={(items) => {
                      fork(handleDropped(items));
                    }}
                    hint="Drop a folder to upload it recursively. Each file is uploaded as it arrives."
                  />

                  <FileTree
                    files={draftFiles}
                    onDelete={(path) => {
                      fork(handleDelete(path));
                    }}
                    busyPaths={busyDeletes}
                    emptyState="Draft is empty. Drop files above to add them."
                  />

                  <div
                    className="t-xs t-muted"
                    style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}
                  >
                    <span>{stagedCount} staged</span>
                    <span>{uploadingCount} uploading</span>
                    {removed.size > 0 ? <span>{removed.size} removed</span> : null}
                    {failedCount > 0 ? (
                      <span style={{ color: 'var(--status-danger)' }}>{failedCount} failed</span>
                    ) : null}
                  </div>

                  <p className="t-xs t-muted" style={{ margin: 0 }}>
                    Tip: refreshing the page resets the local view but the draft on the server keeps
                    every uploaded file until you commit or delete the set.
                  </p>
                </>
              )}
            </div>
          </section>
        </div>
      ) : null}

      {tab === 'files' ? (
        <p className="t-xs t-muted" style={{ marginTop: 'var(--sp-4)' }}>
          Created {formatDateTime(set.data.createdAt)} · Updated{' '}
          {formatDateTime(set.data.updatedAt)} ·{' '}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ display: 'inline-flex', height: 'auto', padding: 0 }}
            onClick={async () => {
              await Promise.all([
                qc.invalidateQueries({ queryKey: ['document-set', setId] }),
                qc.invalidateQueries({ queryKey: ['document-set-versions', setId] }),
                qc.invalidateQueries({ queryKey: ['document-set-version', setId, 'latest'] }),
              ]);
            }}
          >
            Refresh
          </button>
        </p>
      ) : null}

      <Modal
        open={openCommit}
        onClose={() => {
          if (!commitDraft.isPending) setOpenCommit(false);
        }}
        title="Commit draft"
        eyebrow="DOCUMENT SETS · COMMIT"
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setOpenCommit(false)}
              disabled={commitDraft.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => {
                fork(submitCommit());
              }}
              disabled={commitDraft.isPending || !commitName.trim()}
            >
              {commitDraft.isPending ? 'Committing…' : 'Commit'}
            </button>
          </>
        }
      >
        <div style={stack3}>
          <p className="t-secondary" style={{ margin: 0 }}>
            Commit freezes the draft as a new immutable version. Pick a name unique within this set
            (e.g. <code>v1</code>, <code>v2-asset-hierarchy</code>).
          </p>
          <label className="field">
            <span className="field-label">Version name</span>
            <input
              className="input"
              value={commitName}
              onChange={(e) => setCommitName(e.target.value)}
              placeholder="v1"
              autoFocus={true}
              required={true}
            />
          </label>
          <label className="field">
            <span className="field-label">Description (optional)</span>
            <textarea
              className="textarea"
              rows={3}
              value={commitDescription}
              onChange={(e) => setCommitDescription(e.target.value)}
              placeholder="What changed since the previous version?"
            />
          </label>
          {commitDraft.error ? (
            <div className="alert alert--danger">
              Failed to commit: {(commitDraft.error as Error).message}
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

export type { IManifestEntry };
